/**
 * /admin/admins — superadmin-only grant management surface (#1022
 * Slice 1).
 *
 * Mirrors `/admin/orgs` shape (look the existing page up — it's the
 * canonical per-entity admin page template): a directory table with
 * Add / Revoke affordances at the per-row level and a single Add
 * dialog. Each row shows grantee email, grantor email (or "System
 * (env bootstrap)" for the sentinel grantor), and grantedAt; the
 * Revoke control on the requester's own row is DISABLED with a
 * tooltip — defense in depth alongside the server's 403 refusal.
 *
 * The frontend's `<RequireSuperadmin>` wrapper around `/admin/*` is
 * UX defense-in-depth; the actual data boundary is the server's
 * `requireSession + requireSuperadmin` chain on `/api/admin/superadmin-
 * grants`, which now reads the grant collection on every request.
 */
import { useEffect, useMemo, useState, type FormEvent } from 'react'
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui'
import { adminSuperadminGrantsAPI, type AdminSuperadminGrantRow } from '@/lib/api'
import { useIdentityReady } from '@/hooks/useIdentity'
import { useUserTimezone } from '@/hooks/useUserTimezone'
import { useConfirm } from '@/hooks/useConfirm'
import { formatLocalDateTime } from '@/lib/utils'
import { AdminsSubNav } from './AdminsSubNav'

/** Sentinel string the server stamps on env-bootstrap grants. Keep in
 * sync with `SYSTEM_BOOTSTRAP_GRANTOR` in the server-side grant
 * repo — the value flows verbatim from the wire payload. */
const SYSTEM_BOOTSTRAP_GRANTOR = 'system:bootstrap'
/** Same, for the grant the earliest account gets when nobody was named.
 * Keep in sync with `FIRST_ACCOUNT_GRANTOR` on the server. */
const FIRST_ACCOUNT_GRANTOR = 'system:first-account'

function formatGrantor(row: AdminSuperadminGrantRow): string {
  if (row.grantedBy === SYSTEM_BOOTSTRAP_GRANTOR) return 'System (env bootstrap)'
  if (row.grantedBy === FIRST_ACCOUNT_GRANTOR) return 'System (first account)'
  if (row.grantedByEmail) return row.grantedByEmail
  // Grantor User row was hard-deleted — surface the id so the
  // operator can correlate via audit log.
  return `(deleted user: ${row.grantedBy})`
}

export function AdminsPage() {
  const { user: currentUser } = useIdentityReady()
  const tz = useUserTimezone()
  // #1148: shared themed confirm, replacing native window.confirm().
  const confirm = useConfirm()
  const [rows, setRows] = useState<AdminSuperadminGrantRow[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [revokingUserId, setRevokingUserId] = useState<string | null>(null)
  const [revokeError, setRevokeError] = useState<string | null>(null)

  async function reload(): Promise<void> {
    setLoading(true)
    try {
      const r = await adminSuperadminGrantsAPI.list()
      setRows(r)
      setLoadError(null)
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Failed to load grants')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void reload()
  }, [])

  async function handleRevoke(userId: string): Promise<void> {
    if (userId === currentUser.id) return // belt-and-braces — button is disabled too
    const ok = await confirm({
      title: 'Remove Global Admin access?',
      description: 'The user will lose access on their next request.',
      confirmLabel: 'Revoke',
      destructive: true,
    })
    if (!ok) return
    setRevokingUserId(userId)
    setRevokeError(null)
    try {
      await adminSuperadminGrantsAPI.revoke(userId)
      await reload()
    } catch (e) {
      setRevokeError(e instanceof Error ? e.message : 'Failed to revoke grant')
    } finally {
      setRevokingUserId(null)
    }
  }

  const sortedRows = useMemo(() => rows ?? [], [rows])

  if (loading && !rows) {
    return (
      <div className="space-y-6 max-w-full">
        <AdminsSubNav />
        <p className="text-sm text-muted-foreground">Loading grants…</p>
      </div>
    )
  }
  if (loadError && !rows) {
    return (
      <div className="space-y-6 max-w-full">
        <AdminsSubNav />
        <p className="text-sm text-destructive" role="alert">
          {loadError}
        </p>
      </div>
    )
  }
  if (!rows) return null

  return (
    <div className="space-y-6 max-w-full">
      <AdminsSubNav />
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Admins</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage who is a Global Admin on this installation. A change takes
            effect the next time that person does anything. You cannot
            remove yourself.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>Add admin</Button>
      </div>

      {revokeError && (
        <p className="text-sm text-destructive" role="alert">
          {revokeError}
        </p>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Active grants ({rows.length})</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="border rounded-md">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Granted by</TableHead>
                  <TableHead>Granted at</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedRows.map((row) => {
                  const isSelf = row.userId === currentUser.id
                  return (
                    <TableRow key={row.id}>
                      <TableCell className="font-medium">
                        {row.email ?? (
                          <span className="text-muted-foreground italic">
                            (deleted user)
                          </span>
                        )}
                        {isSelf && (
                          <span className="ml-2 text-xs text-muted-foreground">(you)</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">{formatGrantor(row)}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {formatLocalDateTime(row.grantedAt, tz)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={isSelf || revokingUserId === row.userId}
                          title={isSelf ? 'You cannot revoke your own grant' : undefined}
                          onClick={() => void handleRevoke(row.userId)}
                        >
                          {revokingUserId === row.userId ? 'Revoking…' : 'Revoke'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  )
                })}
                {sortedRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-sm text-muted-foreground py-8">
                      No active grants. Click "Add admin" to grant the first one.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <AddAdminDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={() => {
          setCreateOpen(false)
          void reload()
        }}
      />
    </div>
  )
}

// ── Add modal ─────────────────────────────────────────────────────────

function AddAdminDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreated: () => void
}) {
  const [email, setEmail] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setEmail('')
      setError(null)
      setSubmitting(false)
    }
  }, [open])

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!email.trim()) return
    setSubmitting(true)
    setError(null)
    try {
      await adminSuperadminGrantsAPI.create(email.trim())
      onCreated()
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't make this person a Global Admin.")
    } finally {
      setSubmitting(false)
    }
  }

  const canSubmit = email.trim().length > 0 && !submitting

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Add admin</DialogTitle>
            <DialogDescription>
              Make an existing user a Global Admin, by their email address.
              The user must already have an OttoTester account.
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="new-admin-email">Email</Label>
              <Input
                id="new-admin-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@example.com"
                disabled={submitting}
                autoFocus
                required
              />
            </div>
            {error && (
              <p className="text-sm text-destructive" role="alert">
                {error}
              </p>
            )}
          </DialogBody>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {submitting ? 'Granting…' : 'Make Global Admin'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default AdminsPage
